import { CONSTANTS_TIME__client } from '@/constants/CONSTANTS_TIME__client';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_user, use_slice_words } from '@/hooks/store/use_slices';
import { local_words_translating_in_progress_add } from '@/state/words/translate_from_english/local_words_translating_in_progress_add';
import { remote_words_translate_from_english_with_context } from '@/state/words/translate_from_english/remote_words_translate_from_english_with_context';
import { RequestWordsTranslateFromEnglish } from '@shared/dto/+word/word_translated/_requests/request_words_translate_from_english';
import { DTO__word_with_context } from '@shared/dto/+word/word_translated/DTO__word_with_context';
import { DTO__token } from '@shared/dto/generation/settings/token/DTO__token';
import { ENUM__language_type } from '@shared/enums/ENUM__language_type';
import { timestamp_ms } from '@shared/utils/utils_time';
import { useRef } from 'react';

interface Props {
  entity_type: string;
  entity_id: string;
  tokens_to_translate: DTO__token[];
}

export function WatcherTranslateTokensFromEnglishBase({ tokens_to_translate, entity_type, entity_id }: Props) {
  const dispatch = use_dispatch();
  const { user_settings } = use_slice_user(['user_settings']);
  const { words_translating_in_progress } = use_slice_words(['words_translating_in_progress']);

  const last_load_data_at = useRef(NaN);

  /*
   * EFFECTS
   */
  use_effect(() => {
    if (user_settings.regional_language == ENUM__language_type.ENGLISH) return; //no need to translate from english
    if (tokens_to_translate.is_empty()) return; //nothing to translate

    const timestamp = timestamp_ms();
    if (!isNaN(last_load_data_at.current) && timestamp - last_load_data_at.current < CONSTANTS_TIME__client.THROTTLE_TRANSLATE_TOKENS_FROM_ENGLISH) return;

    last_load_data_at.current = timestamp;

    let words_to_translate = DTO__word_with_context.get_words_without_translations(tokens_to_translate, user_settings.regional_language);
    const words_translating_in_progress_on_language = words_translating_in_progress.get(user_settings.regional_language) || new Set();

    words_to_translate = words_to_translate.filter(word_with_context => !words_translating_in_progress_on_language.has(word_with_context.word));

    void try_translate_words(words_to_translate);
  }, [tokens_to_translate, user_settings.regional_language]);

  /*
   * METHODS
   */

  async function try_translate_words(words_to_translate: DTO__word_with_context[]) {
    if (words_to_translate.is_empty()) return;

    let words_strings = words_to_translate.map(word_with_context => word_with_context.word);
    dispatch(local_words_translating_in_progress_add({ words: words_strings, language: user_settings.regional_language }));

    let requests = [];

    for (let word of words_to_translate) {
      let request = new RequestWordsTranslateFromEnglish();

      request.entity_type = entity_type;
      request.entity_id = entity_id;
      request.language = user_settings.regional_language;
      request.word_with_context = word;

      requests.push(request);
    }

    await dispatch(remote_words_translate_from_english_with_context(requests));
  }

  return null;
}
